import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  getAuthStorage,
  getSupabaseAccessToken,
  getSupabaseRefreshToken,
  setSessionPersistence,
} from '@/shared/clients/supabaseClient';
import { registerAuthBridge } from '@/shared/lib/authBridge';
import { clearFamilyContextCache } from '@/shared/lib/familyContext';
import { clearAllMutations } from '@/shared/lib/offlineMutationQueue';
import {
  purgeLegacyResponseCaches,
  purgeResponseCaches,
} from '@/shared/lib/responseCache';
import { resetAllFamilyScopedStores } from '@/shared/lib/storeReset';
import { monitoringService } from '@/shared/services';
import { authService } from '../services/authService';
import type {
  AuthStore,
  LoginCredentials,
  RegisterData,
} from '../types/auth.types';

/**
 * Authentication Store
 * Global state management for user authentication using Zustand
 */

const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isInitialized: false,
  isRecoverySession: false,
  loading: false,
  error: null,
  rememberMe: false,
  redirectPath: null,
};

/**
 * Apply the „Запомни ме" choice for a flow that is about to authenticate, and
 * hand back the undo.
 *
 * The persistence mode is MODULE-GLOBAL state in `supabaseClient` — one
 * switchable adapter shared by the Supabase session and by this store's
 * persisted slice — so whatever the last caller left behind is what the next
 * flow silently inherits. Two consequences follow, and this helper closes
 * both:
 *
 *  - An attempt that never authenticated has no right to move the mode. Call
 *    the returned undo from the failure path so a wrong password does not
 *    latch „Запомни ме" (or its absence) onto whatever runs next.
 *  - Every entry point must state its own choice rather than inherit one.
 *    Registration and the invite flow have no „Запомни ме" control, so they
 *    pin the value the rest of the feature declares as the default
 *    (`initialState.rememberMe`, `LoginForm`'s default, and `loginSchema`'s
 *    `.default(false)`) — the safe direction on a shared family device.
 */
function applySessionPersistence(remember: boolean): () => void {
  const previous = getAuthStorage().getMode();
  setSessionPersistence(remember);
  return () => {
    getAuthStorage().setMode(previous);
  };
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Login user with credentials. The role comes from invitation/membership
       * data (auth metadata + family hydration) — never inferred (AC-30).
       */
      login: async (credentials: LoginCredentials) => {
        set({ loading: true, error: null });

        // Decide WHERE the session is stored before it exists: the Supabase
        // SDK and this store share one switchable storage adapter, so an
        // unchecked „Запомни ме" keeps both the SDK session and the persisted
        // slice below in sessionStorage — gone when the browser closes.
        const restorePersistence = applySessionPersistence(
          credentials.rememberMe ?? false
        );

        try {
          const response = await authService.login(
            credentials.username,
            credentials.password
          );

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            loading: false,
            error: null,
            rememberMe: credentials.rememberMe ?? false,
          });

          // Identify user in monitoring tools
          monitoringService.identifyUser(response.user.id, {
            name: response.user.name,
            email: response.user.email,
            role: response.user.role,
          });

          // Track successful login
          monitoringService.trackEvent('User Logged In', {
            role: response.user.role,
            rememberMe: credentials.rememberMe ?? false,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Неуспешно влизане. Моля, опитайте отново.';

          // This attempt never authenticated, so its „Запомни ме" choice was
          // never exercised. Put the mode back rather than leaving it latched
          // for the next flow — a mistyped password with the box ticked used
          // to make a subsequent registration permanent on a shared device.
          restorePersistence();

          set({
            loading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null,
          });

          // Capture login errors
          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'login' },
              extra: { username: credentials.username },
              level: 'error',
            });
          }

          throw error;
        }
      },

      /**
       * Logout user and clear authentication state
       */
      logout: async () => {
        // Track logout before clearing state
        monitoringService.trackEvent('User Logged Out');

        // Clear user identification
        monitoringService.clearUser();

        try {
          await authService.logout();
        } catch (error) {
          // Capture but continue with local logout cleanup
          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'logout' },
              level: 'warning',
            });
          }
        } finally {
          // Security: drop any queued offline mutations and cached family
          // scope so a subsequent (possibly different) user never replays the
          // previous user's writes or inherits their family_id.
          clearFamilyContextCache();
          resetAllFamilyScopedStores();
          void clearAllMutations();

          // …and the service worker's response caches, which none of the
          // above reaches: Cache Storage is origin-scoped and on disk, so the
          // private album bodies it holds outlive every store, both Web
          // Storage areas and the session itself. See shared/lib/responseCache.
          void purgeResponseCaches();

          // Signed out as far as the UI is concerned — do this BEFORE the
          // teardown below so nothing user-visible waits on a websocket.
          set({
            ...initialState,
            isInitialized: true,
          });

          // Tear down any live realtime channels so the next session never
          // inherits the previous user's family-scoped subscriptions.
          //
          // Awaited, unlike the purges above, because this one has an ordering
          // requirement: a sign-in in the SAME page session re-subscribes as
          // soon as `useRealtimeScope` resolves, and an unfinished
          // `removeAllChannels()` from the previous user would then close the
          // new user's channels with nothing left to trigger another resolve.
          // Resolving `logout()` only once the channels are gone is what makes
          // that ordering hold. `disconnectAllRealtime` bounds its own wait
          // and never rejects, so this cannot hang or fail the sign-out.
          await import('@/shared/clients/supabaseClient')
            .then(({ disconnectAllRealtime }) => disconnectAllRealtime())
            .catch(() => undefined);
        }
      },

      /**
       * Register new user
       */
      register: async (data: RegisterData) => {
        set({ loading: true, error: null });

        // Registration signs the new account in, so it decides where that
        // session is stored — and it has no „Запомни ме" control to decide
        // with. State the default explicitly instead of inheriting the mode a
        // previous login left in the shared adapter: on a family tablet, a
        // parent who ticked the box an hour ago must not silently make a
        // child's brand-new account permanent. Add the checkbox to
        // `RegisterForm` and thread it through `RegisterData` if registration
        // should be rememberable.
        const restorePersistence = applySessionPersistence(false);

        try {
          const response = await authService.register(data);

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            loading: false,
            error: null,
            rememberMe: false,
          });

          // Identify user in monitoring tools
          monitoringService.identifyUser(response.user.id, {
            name: response.user.name,
            email: response.user.email,
            role: response.user.role,
          });

          // Track successful registration
          monitoringService.trackEvent('User Registered', {
            role: response.user.role,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Неуспешна регистрация. Моля, опитайте отново.';

          // Nothing was signed in, so nothing earned the right to move the
          // mode — same reasoning as the failed-login path above.
          restorePersistence();

          set({
            loading: false,
            error: errorMessage,
          });

          // Capture registration errors
          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'register' },
              level: 'error',
            });
          }

          throw error;
        }
      },

      /**
       * Refresh authentication token
       */
      refreshAuthToken: async () => {
        // Read the SAME source the auth bridge advertises at the bottom of
        // this file: the live SDK session first, store state second. The two
        // must not disagree, because `apiClient` gates on the bridge and then
        // calls this action — `if (auth.getRefreshToken()) { await
        // auth.refreshAuthToken() } catch { logout() }`.
        //
        // `refreshToken` is deliberately absent from `partialize` (we never
        // persist raw JWTs), so on any boot where the SDK session is live but
        // `checkAuth` has not yet mirrored it into the store, the gate passed
        // on the SDK token and this action threw 'No refresh token available'
        // on the empty store field. `apiClient` reads that throw as a failed
        // refresh and signs a perfectly valid session out.
        const currentRefreshToken =
          getSupabaseRefreshToken() ?? get().refreshToken;

        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        set({ loading: true, error: null });

        try {
          const response = await authService.refreshToken(currentRefreshToken);

          set({
            token: response.token,
            refreshToken: response.refreshToken ?? currentRefreshToken,
            loading: false,
            error: null,
          });
        } catch (error) {
          // Refresh failed, logout user
          await get().logout();
          throw error;
        }
      },

      /**
       * Check authentication status on app initialization
       */
      checkAuth: async () => {
        // The Supabase SDK owns session persistence. Hydrate from the live
        // session rather than any persisted token (we do not persist raw
        // JWTs).
        set({ loading: true });

        // Boot-time eviction of the response caches an OLDER service worker
        // wrote and this build no longer produces (`supabase-rest`,
        // `api-cache`). It belongs on the boot path rather than on a teardown
        // because the device that still holds those bodies is precisely the
        // one that never signs out — no teardown will ever run on it.
        // Workbox's `cleanupOutdatedCaches` does not cover them: it only ever
        // touched the precache.
        void purgeLegacyResponseCaches();

        try {
          const { getSupabaseClient, isRecoverySession } =
            await import('@/shared/clients/supabaseClient');
          const {
            data: { session },
          } = await getSupabaseClient().auth.getSession();

          if (!session) {
            set({ ...initialState });
            return;
          }

          // Read the recovery flag only AFTER `getSession()` has resolved:
          // it awaits the SDK's own initialization, so by this point the
          // synchronously-registered `PASSWORD_RECOVERY` handler in
          // `supabaseClient` has already run. This is the PRIMARY source of
          // the flag — `initAuthListener` subscribes asynchronously and can
          // miss the event entirely, so this read is not redundant with it.
          const recoverySession = isRecoverySession();

          // The profile read is the only NETWORK round trip left in this
          // function, and it is the one that used to sign people out. Sharing
          // the outer catch with `getSession()` made a DNS blip, an offline
          // boot and a revoked JWT indistinguishable: all three landed on
          // `set({ ...initialState })`, which drops the UI to /login while the
          // session proven live three lines above is still in storage and
          // `getSupabaseAccessToken()` still returns a token for it.
          //
          // A transient failure therefore keeps the session-derived state and
          // the `user` the persisted slice rehydrated. A genuinely dead
          // session is not left hanging on that: the SDK flattens the
          // AuthError into a plain Error here (no `status` to branch on), so
          // convergence comes from the two mechanisms that already exist —
          // the next API call 401s into `apiClient`'s refresh-then-logout, and
          // auth-js emits SIGNED_OUT to `initAuthListener` when its own
          // background refresh fails for good.
          try {
            const user = await authService.getCurrentUser();

            set({
              user,
              token: session.access_token,
              refreshToken: session.refresh_token,
              isAuthenticated: true,
              isRecoverySession: recoverySession,
              error: null,
            });

            monitoringService.identifyUser(user.id, {
              name: user.name,
              email: user.email,
              role: user.role,
            });
          } catch (profileError) {
            // With no rehydrated user there is nothing to render the
            // signed-in shell with, so /login remains the honest answer.
            if (get().user === null) {
              set({ ...initialState });
              return;
            }

            set({
              token: session.access_token,
              refreshToken: session.refresh_token,
              isAuthenticated: true,
              isRecoverySession: recoverySession,
              error: null,
            });

            if (profileError instanceof Error) {
              monitoringService.captureError(profileError, {
                tags: { feature: 'auth', action: 'check-auth-profile' },
                level: 'warning',
              });
            }
          }
        } catch {
          // No valid session, clear state
          set({ ...initialState });
        } finally {
          // `isInitialized` gates EVERY route (ProtectedRoute, PublicRoute,
          // OnboardingGate, /reset-password, /accept-invite), so a checkAuth
          // that fails between the branches above would strand the app on a
          // permanent LoadingScreen — including /login and /forgot-password,
          // the way back in. Flip it here so no exit path can skip it.
          set({ isInitialized: true, loading: false });
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Set redirect path for after login
       */
      setRedirectPath: (path: string | null) => {
        set({ redirectPath: path });
      },

      /**
       * Begin a third-party OAuth sign-in flow.
       *
       * Triggers a full-page redirect to the provider, so the lines after the
       * service call typically never run; the session is completed later on
       * `/auth/callback`.
       */
      signInWithOAuth: async (provider) => {
        set({ loading: true, error: null });

        try {
          await authService.signInWithOAuth(provider);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Неуспешно влизане с външен доставчик.';

          set({
            loading: false,
            error: errorMessage,
            isAuthenticated: false,
          });

          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'oauth-start' },
              extra: { provider },
              level: 'error',
            });
          }

          throw error;
        }
      },

      /**
       * Complete an OAuth session after returning from the provider callback.
       * Used by the `/auth/callback` route. The Supabase SDK has already
       * exchanged the PKCE code (detectSessionInUrl); this reads the session
       * and mirrors it into app state.
       */
      completeOAuthSession: async () => {
        set({ loading: true, error: null });

        try {
          const response = await authService.completeOAuthSession();

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            loading: false,
            error: null,
          });

          monitoringService.identifyUser(response.user.id, {
            name: response.user.name,
            email: response.user.email,
            role: response.user.role,
          });

          monitoringService.trackEvent('User Logged In', {
            role: response.user.role,
            method: 'oauth',
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Неуспешно завършване на влизането.';

          set({
            loading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null,
          });

          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'oauth-complete' },
              level: 'error',
            });
          }

          throw error;
        }
      },

      /**
       * Finalise an email-invited account (/accept-invite). The invite link
       * already established the session; this applies the user's chosen
       * identity + password and mirrors the refreshed user into app state.
       */
      completeInvite: async (data) => {
        set({ loading: true, error: null });

        // The invite link already established the session, so the adapter has
        // already written it somewhere — pin the mode here and the migration
        // in `setMode` carries those keys across. Same reasoning as `register`:
        // /accept-invite has no „Запомни ме" control, and the person finishing
        // an invite on the family tablet is exactly who must not be remembered
        // by default.
        const restorePersistence = applySessionPersistence(false);

        try {
          const response = await authService.completeInvitedAccount(data);

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            loading: false,
            error: null,
            rememberMe: false,
          });

          monitoringService.identifyUser(response.user.id, {
            name: response.user.name,
            email: response.user.email,
            role: response.user.role,
          });

          monitoringService.trackEvent('User Registered', {
            role: response.user.role,
            method: 'invite',
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Неуспешно завършване на акаунта.';

          // The invite was not finished, so leave the mode as we found it.
          restorePersistence();

          set({ loading: false, error: errorMessage });

          if (error instanceof Error) {
            monitoringService.captureError(error, {
              tags: { feature: 'auth', action: 'invite-complete' },
              level: 'error',
            });
          }

          throw error;
        }
      },

      /**
       * Subscribe to live Supabase auth-state changes so the store mirrors
       * sign-in / sign-out / token-refresh that happen outside the explicit
       * action calls (background token refresh, cross-tab sign-in/out, OAuth
       * callback).
       *
       * The callback only ever writes state directly — it never re-invokes the
       * `login`/`logout` actions — so mirroring our own actions cannot recurse.
       */
      initAuthListener: () => {
        let disposed = false;
        let unsubscribe: (() => void) | null = null;

        void import('@/shared/clients/supabaseClient').then(
          ({ onSupabaseAuthStateChange }) => {
            if (disposed) {
              return;
            }

            unsubscribe = onSupabaseAuthStateChange((event, session) => {
              if (event === 'SIGNED_OUT' || !session) {
                // Avoid redundant resets when already signed out. The
                // `initialState` spread below also clears `isRecoverySession`
                // — a recovery session must never outlive its sign-out.
                if (get().isAuthenticated || get().user) {
                  clearFamilyContextCache();
                  resetAllFamilyScopedStores();
                  void clearAllMutations();
                  // Same purge as the explicit `logout` path: this branch is
                  // the sign-out nobody clicked (a cross-tab sign-out, an
                  // expired session, a refresh that failed for good), and it
                  // hands the device over just as completely.
                  void purgeResponseCaches();
                  monitoringService.clearUser();
                  set({ ...initialState, isInitialized: true });
                }
                return;
              }

              // Identity guard — the store's tokens must always belong to the
              // store's user, and this tab must never claim a session it does
              // not hold. auth-js broadcasts auth events over a
              // `BroadcastChannel` keyed by the PROJECT's storage key, not by
              // user, so another tab's sign-in or recovery arrives here as an
              // ordinary event carrying THAT user's session. The receiving tab
              // only notifies subscribers — it never writes that session to
              // its own client. So mirroring a broadcast would either send a
              // foreign JWT (evaluated by RLS as the other user) while `user`
              // still describes ours, or — when „Запомни ме" put the other
              // tab's session in ITS sessionStorage — mark us authenticated
              // with no session at all, mounting the whole app over requests
              // that carry only the anon key.
              //
              // Neither the broadcast session nor the absence of a local user
              // is the answer: `checkAuth` re-derives our real state from the
              // SDK's OWN session. Deferred to a macrotask because auth-js
              // holds its lock while notifying subscribers, and calling back
              // into the SDK from inside the callback can deadlock.
              const currentUser = get().user;
              if (!currentUser || session.user?.id !== currentUser.id) {
                setTimeout(() => {
                  if (!disposed) {
                    void get().checkAuth();
                  }
                }, 0);
                return;
              }

              // A recovery link establishes an ordinary-looking session, so
              // without this branch `/reset-password` cannot tell it apart
              // from a normal sign-in and would let anyone at an unlocked
              // browser set a new password. SUPPLEMENT ONLY: this listener is
              // attached asynchronously (`void import(...)` above) and may
              // miss the event — `checkAuth` reads the same flag from the
              // client module after `getSession()` resolves and is the
              // primary path. Do not delete that read as redundant.
              if (event === 'PASSWORD_RECOVERY') {
                set({ isRecoverySession: true });
              }

              // Mirror the freshest tokens (covers TOKEN_REFRESHED). NOTE:
              // `isInitialized` is deliberately NOT set here — `checkAuth`'s
              // `finally` is its only writer on this path. Setting it from a
              // broadcast lets a reload of /reset-password paint the
              // "already signed in" panel with `isRecoverySession` still
              // false, before `checkAuth` has read the recovery marker.
              set({
                token: session.access_token,
                refreshToken: session.refresh_token,
                isAuthenticated: true,
              });
            });
          }
        );

        return () => {
          disposed = true;
          unsubscribe?.();
        };
      },

      updateAvatar: async (avatarUrl: string) => {
        const current = get().user;
        if (!current) {
          return;
        }
        const updated = await authService.updateAvatar(avatarUrl);
        set({ user: updated });
      },

      setUserRole: (role) => {
        const current = get().user;
        if (!current || current.role === role) {
          return;
        }
        set({ user: { ...current, role } });
      },
    }),
    {
      name: 'auth-storage',
      // Same switchable adapter the Supabase client uses, so this entry obeys
      // „Запомни ме" too: with the box unchecked it is written to
      // sessionStorage and dies with the browser session. Persisting it in
      // localStorage regardless would still "remember" the user across
      // launches — the shell would flash before the redirect.
      storage: createJSONStorage(() => getAuthStorage()),
      partialize: (state) => ({
        user: state.user,
        // Never persist raw tokens — the Supabase SDK owns session
        // persistence (PKCE); duplicating JWTs in storage is forbidden.
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
    }
  )
);

// Register the auth implementation with the shared auth bridge so shared
// infrastructure (e.g. the API client) can authenticate without importing
// this feature directly.
//
// Tokens come from the live Supabase SDK session (cached in
// `supabaseClient`), falling back to store state during the brief window
// before the session cache is warmed.
registerAuthBridge({
  getToken: () => getSupabaseAccessToken() ?? useAuthStore.getState().token,
  getRefreshToken: () =>
    getSupabaseRefreshToken() ?? useAuthStore.getState().refreshToken,
  refreshAuthToken: () => useAuthStore.getState().refreshAuthToken(),
  logout: () => useAuthStore.getState().logout(),
});
